import React from 'react';

const LicensePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            QuickReach AI Brain Source-Available Commercial License
          </h1>
          <p className="text-xl text-gray-600">Version 1.0</p>
          <p className="text-lg text-gray-500">Effective Date: January 1, 2025</p>
          <p className="text-lg text-gray-500">Software: QuickReach AI Brain</p>
          <p className="text-lg text-gray-500 font-semibold">Copyright © 2025 QuickReach Pte Ltd. All rights reserved.</p>
        </div>

        {/* License Content */}
        <div className="bg-white rounded-xl shadow-lg p-8 space-y-8">
          
          {/* 1. Grant of Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Grant of Rights</h2>
            <p className="text-gray-700 mb-4">
              Subject to the terms and conditions of this License, QuickReach Pte Ltd hereby grants you a non-exclusive, non-transferable, revocable license to:
            </p>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Permitted Uses:</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-green-500 mr-2 mt-1">✅</span>
                <span>Use the Software for your internal business operations</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2 mt-1">✅</span>
                <span>Access and review the source code for transparency, security audits, and compliance</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2 mt-1">✅</span>
                <span>Modify the source code to meet your specific internal requirements</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2 mt-1">✅</span>
                <span>Deploy the Software on your own infrastructure or cloud environment</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2 mt-1">✅</span>
                <span>Create internal documentation and training materials</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2 mt-1">✅</span>
                <span>Perform security testing and vulnerability assessments</span>
              </li>
            </ul>
          </section>

          {/* 2. Restrictions */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Restrictions</h2>
            <p className="text-gray-700 mb-4">You are expressly prohibited from:</p>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Commercial Restrictions:</h3>
            <ul className="space-y-2 text-gray-700 mb-6">
              <li className="flex items-start">
                <span className="text-red-500 mr-2 mt-1">❌</span>
                <span>Selling, licensing, or distributing the Software to any third party</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2 mt-1">❌</span>
                <span>Creating derivative works for commercial distribution or sale</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2 mt-1">❌</span>
                <span>Offering the Software as a service to external customers</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2 mt-1">❌</span>
                <span>Using the Software to build competing products or services</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2 mt-1">❌</span>
                <span>Reverse engineering the Software for competitive intelligence</span>
              </li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Technical Restrictions:</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-red-500 mr-2 mt-1">❌</span>
                <span>Removing or modifying copyright notices, trademarks, or attribution</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2 mt-1">❌</span>
                <span>Distributing modified versions outside your organization</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2 mt-1">❌</span>
                <span>Using QuickReach trademarks without explicit written permission</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2 mt-1">❌</span>
                <span>Decompiling or disassembling compiled components (where applicable)</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2 mt-1">❌</span>
                <span>Creating public forks or repositories of the source code</span>
              </li>
            </ul>
          </section>

          {/* 3. Definitions */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Definitions</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Commercial Use</h3>
                <p className="text-gray-700 mb-2">
                  "Commercial use" means any use that is primarily intended for or directed toward commercial advantage or monetary compensation by parties other than the Licensee, including but not limited to:
                </p>
                <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                  <li>Selling or licensing the Software</li>
                  <li>Providing Software-as-a-Service offerings</li>
                  <li>Creating competing products using the Software's code or algorithms</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Internal Use</h3>
                <p className="text-gray-700">
                  "Internal use" means use of the Software solely within your organization for your own business operations, excluding any distribution or service provision to external parties.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Derivative Works</h3>
                <p className="text-gray-700">
                  "Derivative works" means any work that is based upon the Software or portions thereof, including modifications, adaptations, or improvements.
                </p>
              </div>
            </div>
          </section>

          {/* 4. Customer Obligations */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Customer Obligations</h2>
            <p className="text-gray-700 mb-4">As a condition of this license, you agree to:</p>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Compliance Requirements:</h3>
            <ul className="space-y-2 text-gray-700 mb-6">
              <li className="flex items-start">
                <span className="text-blue-500 mr-2 mt-1">📋</span>
                <span>Maintain accurate records of all modifications made to the Software</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2 mt-1">🔍</span>
                <span>Allow QuickReach to audit compliance with this license upon reasonable notice</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2 mt-1">🚨</span>
                <span>Promptly notify QuickReach Pte Ltd of any discovered security vulnerabilities</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2 mt-1">📝</span>
                <span>Provide written notice to QuickReach Pte Ltd of any intended modifications that may affect core functionality</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2 mt-1">🏢</span>
                <span>Ensure all users within your organization comply with these license terms</span>
              </li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Attribution Requirements:</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-blue-500 mr-2 mt-1">📄</span>
                <span>Retain all copyright notices in source code and documentation</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2 mt-1">🏷️</span>
                <span>Include proper attribution in any internal systems or documentation</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2 mt-1">📋</span>
                <span>Maintain license file with any distributed internal versions</span>
              </li>
            </ul>
          </section>

          {/* 5. Intellectual Property Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Intellectual Property Rights</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Ownership</h3>
                <p className="text-gray-700">
                  QuickReach Pte Ltd retains all right, title, and interest in and to the Software, including all intellectual property rights. This license does not grant you any ownership rights in the Software.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Customer Modifications</h3>
                <p className="text-gray-700 mb-2">You retain ownership of modifications you create, but:</p>
                <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                  <li>Must clearly identify them as modifications</li>
                  <li>Cannot claim ownership of underlying QuickReach Pte Ltd code</li>
                  <li>Grant QuickReach Pte Ltd a non-exclusive right to incorporate improvements</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 6. Support and Updates */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Support and Updates</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Commercial Support</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2 mt-1">🛠️</span>
                    <span>Technical support provided under separate commercial agreement</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2 mt-1">📧</span>
                    <span>Professional services available for customization and integration</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2 mt-1">🎓</span>
                    <span>Training and consultation offered through commercial channels</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Software Updates</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2 mt-1">🔄</span>
                    <span>Updates and patches provided to licensed customers</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2 mt-1">📢</span>
                    <span>Security notifications shared with active license holders</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2 mt-1">🆕</span>
                    <span>New features may require license upgrade or additional fees</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* 7. Warranty and Liability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Warranty and Liability</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Disclaimer of Warranty</h3>
                <p className="text-gray-700 font-semibold">
                  THE SOFTWARE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND. QUICKREACH PTE LTD DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Limitation of Liability</h3>
                <p className="text-gray-700 font-semibold">
                  IN NO EVENT SHALL QUICKREACH PTE LTD BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES ARISING OUT OF THE USE OR INABILITY TO USE THE SOFTWARE.
                </p>
              </div>
            </div>
          </section>

          {/* 8. Term and Termination */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Term and Termination</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Term</h3>
                <p className="text-gray-700">This license remains in effect until terminated by either party.</p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Termination Conditions</h3>
                <p className="text-gray-700 mb-2">This license automatically terminates if you:</p>
                <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                  <li>Breach any terms of this agreement</li>
                  <li>File for bankruptcy or become insolvent</li>
                  <li>Are acquired by a direct competitor of QuickReach Pte Ltd</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Effect of Termination</h3>
                <p className="text-gray-700 mb-2">Upon termination, you must:</p>
                <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                  <li>Cease all use of the Software</li>
                  <li>Delete all copies from your systems</li>
                  <li>Return or destroy any confidential materials</li>
                  <li>Provide written certification of compliance</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 9. Compliance and Monitoring */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Compliance and Monitoring</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Audit Rights</h3>
                <p className="text-gray-700 mb-3">QuickReach Pte Ltd reserves the right to:</p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2 mt-1">🔍</span>
                    <span>Audit your use of the Software with 30 days written notice</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2 mt-1">📊</span>
                    <span>Request usage reports on an annual basis</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2 mt-1">🔐</span>
                    <span>Monitor compliance through technical measures where applicable</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Remediation</h3>
                <p className="text-gray-700 mb-2">If non-compliance is discovered:</p>
                <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                  <li>You have 30 days to cure any breach</li>
                  <li>QuickReach Pte Ltd may terminate license for material breaches</li>
                  <li>Additional licensing fees may apply for unauthorized use</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 10. Commercial Licensing Options */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Commercial Licensing Options</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Enterprise Licensing</h3>
                <p className="text-gray-700 mb-3">For expanded rights including:</p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 mt-1">🌐</span>
                    <span>Multi-organization deployment</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 mt-1">🏢</span>
                    <span>Service provider licensing</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 mt-1">🤝</span>
                    <span>Partnership and reseller rights</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 mt-1">🛡️</span>
                    <span>Enhanced support and warranties</span>
                  </li>
                </ul>
                <p className="text-gray-700 mt-3">
                  <strong>Contact:</strong> licensing@quickreach.com
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Custom Licensing</h3>
                <p className="text-gray-700 mb-2">Specialized license terms available for:</p>
                <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                  <li>Government and defense contractors</li>
                  <li>Academic and research institutions</li>
                  <li>Non-profit organizations</li>
                  <li>Strategic partnerships</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 11. Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Governing Law</h2>
            <p className="text-gray-700 mb-4">
              This license shall be governed by and construed in accordance with the laws of Singapore, without regard to conflict of law principles.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Dispute Resolution</h3>
            <p className="text-gray-700 mb-2">Any disputes arising under this license shall be resolved through:</p>
            <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
              <li>Good faith negotiation</li>
              <li>Binding arbitration under Singapore International Arbitration Centre (SIAC) rules if negotiation fails</li>
              <li>Jurisdiction in Singapore courts for any court proceedings</li>
            </ul>
          </section>

          {/* 12. Contact Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
            <p className="text-gray-700 font-semibold mb-4">QuickReach Pte Ltd Legal Department</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Contact Details</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center">
                    <span className="text-blue-500 mr-2">📧</span>
                    <span>Email: legal@quickreach.com</span>
                  </li>
                  <li className="flex items-center">
                    <span className="text-blue-500 mr-2">📧</span>
                    <span>Licensing: licensing@quickreach.com</span>
                  </li>
                  <li className="flex items-center">
                    <span className="text-blue-500 mr-2">📞</span>
                    <span>Phone: +65 6123 4567</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Office Locations</h3>
                <div className="space-y-4">
                  <div>
                    <p className="font-semibold text-gray-800">Singapore Office (Primary):</p>
                    <p className="text-gray-700">🏢 QuickReach Pte Ltd</p>
                    <p className="text-gray-700">112 Robinson Road, #03-04 Robinson 112</p>
                    <p className="text-gray-700">Singapore 068902</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">Philippines Office:</p>
                    <p className="text-gray-700">🏢 QuickReach Inc.</p>
                    <p className="text-gray-700">L29 Joy Nostalg Center</p>
                    <p className="text-gray-700">17 ADB Avenue, Pasig City</p>
                    <p className="text-gray-700">Philippines 1605</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 13. License Acceptance */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. License Acceptance</h2>
            <p className="text-gray-700 mb-4">
              By using, accessing, or installing the QuickReach AI Brain Software, you acknowledge that you have read, understood, and agree to be bound by the terms and conditions of this License Agreement.
            </p>
            <p className="text-gray-700 font-semibold">Digital Signature Required for Commercial Deployment</p>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-8 mt-8">
            <p className="text-center text-gray-600">
              © 2025 QuickReach Pte Ltd. All rights reserved.
            </p>
            <p className="text-center text-gray-600">
              QuickReach AI Brain™ is a trademark of QuickReach Pte Ltd.
            </p>
          </div>

          {/* Appendices */}
          <div className="border-t border-gray-200 pt-8 mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Appendices</h2>
            
            {/* Appendix A */}
            <section className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Appendix A: Permitted Modification Guidelines</h3>
              
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">Allowed Modifications:</h4>
                  <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                    <li>UI customization and branding</li>
                    <li>Integration with internal systems</li>
                    <li>Performance optimizations</li>
                    <li>Security enhancements</li>
                    <li>Language and localization changes</li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">Restricted Modifications:</h4>
                  <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                    <li>Core AI algorithms and models</li>
                    <li>License validation mechanisms</li>
                    <li>Security and encryption components</li>
                    <li>Proprietary data processing methods</li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">Modification Documentation Requirements:</h4>
                  <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                    <li>Document all changes with version control</li>
                    <li>Maintain separation between QuickReach code and customer code</li>
                    <li>Provide modification summary for support purposes</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Appendix B */}
            <section>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Appendix B: Security and Compliance</h3>
              
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">Security Responsibilities:</h4>
                  <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                    <li>Customer responsible for securing their deployment</li>
                    <li>QuickReach Pte Ltd provides security best practices documentation</li>
                    <li>Vulnerability disclosure process defined</li>
                    <li>Regular security updates provided</li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">Compliance Standards:</h4>
                  <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                    <li>GDPR compliance for EU deployments</li>
                    <li>SOC 2 Type II compliance available</li>
                    <li>Industry-specific compliance support</li>
                    <li>Audit trail requirements for regulated industries</li>
                  </ul>
                </div>
              </div>
            </section>
          </div>

          {/* Final Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
            <p className="text-blue-800 text-center">
              This license agreement has been designed to provide transparency while protecting intellectual property rights. For questions about licensing terms or to discuss custom arrangements, please contact our licensing team.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicensePage;